import React from 'react'
import { MoreHorizontal, MessageCircle, Calendar, AlertTriangle, TrendingUp, TrendingDown, Minus, Eye } from 'lucide-react'
import PerformanceLevelBadge from './PerformanceLevelBadge'
import CircularProgress from '../CircularProgress'

interface TeamMemberCardProps {
  id: string
  name: string
  role: string
  avatar?: string
  score: number
  previousScore?: number
  level: string
  trend: 'up' | 'down' | 'neutral'
  keyMetric?: {
    label: string
    value: string
    color: string
  }
  alertStatus?: 'none' | 'attention' | 'urgent'
  lastUpdated: string
  onActionClick?: (action: string, memberId: string) => void
}

const TeamMemberCard = ({ 
  id,
  name, 
  role, 
  avatar, 
  score, 
  previousScore,
  level, 
  trend,
  keyMetric,
  alertStatus = 'none',
  lastUpdated,
  onActionClick
}: TeamMemberCardProps) => {
  
  const getTrendIcon = () => {
    switch (trend) {
      case 'up': return TrendingUp
      case 'down': return TrendingDown
      default: return Minus
    }
  }

  const getTrendColor = () => {
    switch (trend) {
      case 'up': return 'text-success'
      case 'down': return 'text-error'
      default: return 'text-brand-gray'
    }
  }

  const getAlertIcon = () => {
    if (alertStatus === 'urgent') return '🚨'
    if (alertStatus === 'attention') return '⚠️'
    return null
  }

  const TrendIcon = getTrendIcon()

  return (
    <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10 hover:border-brand-primary/30 transition-all duration-200 hover:shadow-monumenta-brand group relative">
      {/* Alert Indicator */}
      {alertStatus !== 'none' && (
        <div className="absolute -top-2 -right-2">
          <div className={`p-1 rounded-full text-xs ${
            alertStatus === 'urgent' ? 'bg-error' : 'bg-warning'
          } text-white`}>
            {getAlertIcon()}
          </div>
        </div>
      )}

      {/* Header with Avatar and Actions */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          {avatar ? (
            <img 
              src={avatar} 
              alt={name}
              className="w-10 h-10 rounded-full border-2 border-brand-primary/20"
            />
          ) : (
            <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center">
              <span className="text-brand-primary font-display font-medium text-sm">
                {name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </span>
            </div>
          )}
          <div>
            <h3 className="font-display font-semibold text-white group-hover:text-brand-primary transition-colors">
              {name}
            </h3>
            <p className="text-sm text-brand-gray">{role}</p>
          </div>
        </div>

        <div className="flex items-center space-x-1">
          <button 
            className="p-2 rounded-lg text-white hover:text-success hover:bg-success/20 transition-all duration-200 border border-gray-600"
            onClick={() => onActionClick?.('view_details', id)}
            title="Ver detalhes"
          >
            <Eye size={16} />
          </button>
          <button 
            className="p-2 rounded-lg text-white hover:text-brand-blue hover:bg-brand-blue/20 transition-all duration-200 border border-gray-600"
            onClick={() => onActionClick?.('schedule-1on1', id)}
            title="Agendar 1:1"
          >
            <Calendar size={16} />
          </button>
          <button 
            className="p-2 rounded-lg text-white hover:text-brand-primary hover:bg-brand-primary/20 transition-all duration-200 border border-gray-600"
            onClick={() => onActionClick?.('send-feedback', id)}
            title="Enviar feedback"
          >
            <MessageCircle size={16} />
          </button>
          <button 
            className="p-2 rounded-lg text-white hover:text-gray-300 hover:bg-gray-700/50 transition-all duration-200 border border-gray-600"
            onClick={() => onActionClick?.('more-options', id)}
            title="Mais opções"
          >
            <MoreHorizontal size={16} />
          </button>
        </div>
      </div>

      {/* Performance Score and Level */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <CircularProgress percentage={score} size={60} />
          <div>
            <div className="text-2xl font-display font-bold text-brand-primary">
              {score}
            </div>
            <div className={`flex items-center space-x-1 text-sm font-medium ${getTrendColor()}`}>
              <TrendIcon size={14} />
              <span>
                {previousScore ? 
                  `${trend === 'up' ? '+' : trend === 'down' ? '-' : ''}${Math.abs(score - previousScore).toFixed(1)}` 
                  : 'N/A'
                }
              </span>
            </div>
          </div>
        </div>
        
        <PerformanceLevelBadge level={level} score={score} size="sm" />
      </div>

      {/* Key Metric */}
      {keyMetric && (
        <div className="mb-4">
          <div className="bg-brand-dark/30 rounded-monumenta p-3">
            <div className="flex justify-between items-center">
              <span className="text-xs text-brand-gray font-display">
                {keyMetric.label}
              </span>
              <span className={`text-sm font-display font-bold ${keyMetric.color}`}>
                {keyMetric.value}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Footer with Last Updated */}
      <div className="flex items-center justify-between text-xs text-brand-gray border-t border-brand-gray/20 pt-3">
        <span>Atualizado: {lastUpdated}</span>
        {alertStatus !== 'none' && (
          <button 
            className="text-brand-primary hover:text-brand-primary/80 font-medium"
            onClick={() => onActionClick?.('view-alerts', id)}
          >
            Ver alertas
          </button>
        )}
      </div>
    </div>
  )
}

export default TeamMemberCard
